import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";
import { Heartbeat, Database, Cpu, Stack, ArrowsClockwise, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

// Live worker health snapshot. Auto-refreshes every 10 s so admins can watch
// memory + request rate trend during a load event (eg. when delegates flood
// in at conference open). The metrics are best-effort and reset on every
// worker restart - we want trend, not a perfect counter.
const REFRESH_MS = 10_000;
const RSS_WARN_MB = 350;   // anecdotally where DFTB26 production started failing
const RSS_CRIT_MB = 480;
const MONGO_WARN_MS = 50;
const MONGO_CRIT_MS = 200;
const ERR_WARN_PCT = 1.0;
const ERR_CRIT_PCT = 5.0;

function statusBand(value, warn, crit) {
  if (value >= crit) return "crit";
  if (value >= warn) return "warn";
  return "ok";
}
const BAND_BG = { ok: "bg-[#FFFFFF]", warn: "bg-[#FFF7E5]", crit: "bg-[#FFE9E7]" };
const BAND_BORDER = { ok: "border-[#E5E9F0]", warn: "border-[#E0B244]", crit: "border-[#DC4F48]" };
const BAND_TEXT = { ok: "text-[#000000]", warn: "text-[#7A5400]", crit: "text-[#7A2826]" };

function formatUptime(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function MetricCard({ label, value, unit, band = "ok", hint }) {
  return (
    <div
      data-testid={`health-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`p-3 border rounded-sm ${BAND_BG[band]} ${BAND_BORDER[band]}`}
    >
      <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">{label}</div>
      <div className={`font-display text-2xl ${BAND_TEXT[band]}`}>
        {value}
        {unit && <span className="text-sm ml-1 font-mono-time text-[#7A828C]">{unit}</span>}
      </div>
      {hint && <div className="text-[11px] text-[#7A828C] mt-0.5 leading-tight">{hint}</div>}
    </div>
  );
}

export default function HealthAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/health");
      setData(r.data);
      setLastChecked(new Date());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const runBackfill = useCallback(async () => {
    if (backfillBusy) return;
    if (!window.confirm("Migrate every remaining base64 photo / quiz image into object storage now? This is safe to re-run.")) return;
    setBackfillBusy(true);
    try {
      const r = await api.post("/admin/storage/backfill");
      setBackfillResult(r.data?.results || null);
      toast.success("Storage backfill complete.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBackfillBusy(false);
    }
  }, [backfillBusy]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  if (loading && !data) {
    return <div className="py-10 text-center text-[#7A828C] font-mono-time text-xs uppercase tracking-widest">Loading worker health</div>;
  }
  if (!data) return null;

  const rssMb = data.memory?.rss_mb ?? 0;
  const mongoMs = data.mongo?.ping_ms ?? 0;
  const errPct = data.requests?.errors_5xx_pct ?? 0;
  const memBand = statusBand(rssMb, RSS_WARN_MB, RSS_CRIT_MB);
  const mongoBand = statusBand(mongoMs, MONGO_WARN_MS, MONGO_CRIT_MS);
  const errBand = statusBand(errPct, ERR_WARN_PCT, ERR_CRIT_PCT);
  const overallOk = data.ok && memBand === "ok" && mongoBand === "ok" && errBand === "ok";

  return (
    <div className="space-y-4" data-testid="admin-health-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl flex items-center gap-2">
            <Heartbeat size={20} weight="duotone" className="text-[#DC4F48]" />
            Worker health
          </h2>
          <p className="text-sm text-[#7A828C] mt-0.5">
            Refreshes every 10s. Counters reset on every worker restart.
            {lastChecked && <span className="ml-1 font-mono-time text-[11px]">Last: {lastChecked.toLocaleTimeString()}</span>}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          data-testid="health-refresh-btn"
          className="rounded-sm"
        >
          <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <div className={`p-3 border rounded-sm flex items-center gap-3 ${overallOk ? "bg-[#F0FAF5] border-[#43C4A0] text-[#1F5B47]" : "bg-[#FFE9E7] border-[#DC4F48] text-[#7A2826]"}`} data-testid="health-overall-banner">
        {overallOk ? <CheckCircle size={20} weight="fill" /> : <WarningCircle size={20} weight="fill" />}
        <span className="font-medium text-sm">
          {overallOk ? "All systems nominal." : "One or more metrics in warn/crit range. Investigate below."}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricCard
          label="Memory (RSS)"
          value={rssMb}
          unit="MB"
          band={memBand}
          hint={`warn >${RSS_WARN_MB} / crit >${RSS_CRIT_MB}`}
        />
        <MetricCard
          label="Mongo ping"
          value={mongoMs}
          unit="ms"
          band={data.mongo?.ok ? mongoBand : "crit"}
          hint={data.mongo?.ok ? `warn >${MONGO_WARN_MS} / crit >${MONGO_CRIT_MS}` : "mongo unreachable"}
        />
        <MetricCard
          label="5xx error rate"
          value={errPct}
          unit="%"
          band={errBand}
          hint={`${data.requests?.errors_5xx ?? 0} of ${data.requests?.total ?? 0}`}
        />
        <MetricCard
          label="CPU"
          value={data.cpu_percent ?? 0}
          unit="%"
        />
        <MetricCard
          label="Uptime"
          value={formatUptime(data.uptime_seconds || 0)}
        />
        <MetricCard
          label="Total requests"
          value={data.requests?.total ?? 0}
          hint={`${data.requests?.rate_per_second ?? 0}/s avg`}
        />
        {Object.entries(data.requests?.by_status || {}).map(([code, count]) => (
          <MetricCard
            key={code}
            label={`${code} responses`}
            value={count}
            band={code === "5xx" ? "crit" : code === "4xx" ? "warn" : "ok"}
          />
        ))}
      </div>

      <div className="p-3 bg-[#F8F9FB] border border-[#E5E9F0] rounded-sm text-xs text-[#5a5a5a] leading-relaxed space-y-1">
        <div className="flex items-start gap-2">
          <Stack size={14} className="mt-0.5 text-[#7A828C] flex-shrink-0" />
          <div><b>Memory (RSS):</b> resident set size of the worker process. If this climbs steadily it usually means we are loading a base64 blob into memory on a hot endpoint. The DFTB26 production worker started failing around 400 MB.</div>
        </div>
        <div className="flex items-start gap-2">
          <Database size={14} className="mt-0.5 text-[#7A828C] flex-shrink-0" />
          <div><b>Mongo ping:</b> a single tiny find_one round-trip. Above 50 ms means connection pool saturation or Atlas-side slowdown.</div>
        </div>
        <div className="flex items-start gap-2">
          <Cpu size={14} className="mt-0.5 text-[#7A828C] flex-shrink-0" />
          <div><b>5xx error rate:</b> percentage of requests since the worker started. Even 1% sustained is worth investigating.</div>
        </div>
      </div>

      <div className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div>
            <h3 className="font-display text-base">Object storage backfill</h3>
            <p className="text-xs text-[#7A828C] mt-0.5">Migrate any remaining base64 photos and quiz images out of MongoDB and into object storage. Idempotent and safe to re-run.</p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={runBackfill}
            disabled={backfillBusy}
            data-testid="health-backfill-btn"
            className="rounded-sm flex-shrink-0"
          >
            {backfillBusy ? "Migrating..." : "Run backfill"}
          </Button>
        </div>
        {backfillResult && (
          <div className="mt-2 space-y-1 text-xs font-mono-time" data-testid="health-backfill-result">
            {Object.entries(backfillResult).map(([coll, info]) => (
              <div key={coll} className="flex justify-between border-t border-[#E5E9F0] pt-1">
                <span className="uppercase tracking-widest text-[#7A828C]">{coll}</span>
                <span>{info.migrated} migrated{info.failed ? `, ${info.failed} failed` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
